/* How many names (owner, 22 October 2026: "I liked the one where they select how many names they
 * want and it shows the cost being different", then "if they choose 2 and write two names, we
 * should probably show both right?"), on a keychain's or a magnet's page; on a sign's page, just
 * the sign, the sign with its keychain, or two signs (owner: "yes, start the signs version").
 *
 * The choices and their prices come from snippets/names-picker.liquid. With two or three names,
 * a box per name; the box being typed drives the page's one live preview through the real name
 * field (hidden then), exactly as a customer typing there would, and every name keeps the
 * picture the preview drew of it (il-union-preview.js, ilCardSnapshot) in a row under the views:
 * tap one to see it big again. A name is checked once the preview has drawn it and made its
 * print shape (the spec, with the line's other properties); a name typed but not drawn yet is
 * drawn when the customer stops typing, one at a time.
 *
 * The set (a sign with the design's keychain at the set's price): the keychain is drawn by the
 * keychain's own product page section, in a hidden frame (Shopify's Section Rendering API, the
 * product page's main section for the keychain's URL), so its print shape is the one the
 * keychain's page makes; the frame follows the sign's name and colour.
 *
 * Add to cart turns on when every name (and the set's keychain) is checked: il-cart.js asks
 * issue(), then adds one line per item in one request, each with its own shape and picture
 * (lines()).
 *
 * One live preview, one graphics context: Firefox handles one per page well, and on a phone the
 * slowest step is making the shape, so two or three live previews would make the page slow.
 */
const _i18n = (() => { try { const s = document.getElementById('il-i18n'); return s ? JSON.parse(s.textContent) : {}; } catch { return {}; } })();
const t = (key, p = {}) => { const v = _i18n[key] ?? key; return Object.entries(p).reduce((s, [k, val]) => s.replace('{' + k + '}', () => val), String(v)); };
const picker = document.querySelector('[data-names]');
const form = document.getElementById('il-buy');
const real = document.querySelector('[data-text-input][form="il-buy"]');
const root = document.querySelector('[data-union-preview]');
if (picker && form && real && root) init();

function init() {
  const cfg = JSON.parse(picker.querySelector('[data-names-config]').textContent);
  const sign = !!cfg.sign;
  const realBox = real.closest('.inp');
  const slab = real.closest('[data-step="text"]')?.querySelector('.slab');
  // At the top of the name step, which a phone moves to the preview (il-union-preview.js).
  if (slab) slab.before(picker);
  picker.hidden = false;
  const specInput = root.querySelector(sign ? '[data-sign-spec]' : '[data-keychain-spec],[data-magnet-spec]');
  const reviews = sign ? '[data-sign-review]:not(:disabled),[data-support-review]:not(:disabled)' : '[data-keychain-review]:not(:disabled),[data-magnet-review]:not(:disabled)';
  const letters = new RegExp('^(?:' + (real.getAttribute('pattern') || '[A-Za-z]+') + ')$');
  // The fewest letters the design draws (snippets/union-preview.liquid): a shorter name is never drawn.
  let fewest = 1;
  try { fewest = JSON.parse(document.querySelector('[data-union-config]')?.textContent || '{}').minLength || 1; } catch { /* one letter */ }
  const language = document.documentElement.lang || 'en';
  let variants = [];
  try { variants = JSON.parse(document.querySelector('[data-variant-data]')?.textContent || '[]'); } catch { /* the set's keychain takes its first colour */ }

  let count = 1, set = false, active = 0, checkTimer;
  const names = ['', '', ''];
  const checked = [null, null, null];
  const boxes = document.createElement('div');
  boxes.className = 'names__boxes';
  boxes.hidden = true;
  realBox.after(boxes);
  const strip = document.createElement('div');
  strip.className = 'names-strip';
  strip.hidden = true;
  const stripLabel = document.createElement('p');
  stripLabel.className = 'names-strip__label';
  const row = document.createElement('div');
  row.className = 'names-strip__row';
  strip.append(stripLabel, row);
  (root.querySelector('[data-preview-size]') || root.querySelector('[data-preview-gallery]'))?.after(strip);
  let inputs = [];

  const money = cents => window.ilMoney ? window.ilMoney(cents) : new Intl.NumberFormat(language, {style: 'currency', currency: form.dataset.currency || 'EUR'}).format(cents / 100);
  const percentFor = n => cfg.tiers.reduce((best, tier) => n >= tier.min && tier.percent > best ? tier.percent : best, 0);
  // Shopify's percentage drops the fraction of a cent: 10% of 14.99 takes 1.49.
  const unitPrice = (price, n) => price - Math.floor(price * percentFor(n) / 100 + 1e-4);
  const variantId = () => form.querySelector('[data-variant-id]')?.value;
  const price = () => cfg.prices[variantId()] ?? cfg.price;
  const valid = name => !!name && name.length >= fewest && name.length <= real.maxLength && letters.test(name);
  const fresh = i => { const c = checked[i]; return !!c && c.name === names[i].trim(); };
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms, null));

  // The set's keychain: the design's keychain in the sign's colour, where it comes in it.
  function keychainVariant() {
    const chosen = variants.find(v => String(v.id) === String(variantId()));
    const wanted = (chosen?.options || []).map(o => String(o).toLowerCase());
    let best = null, score = -1;
    for (const v of cfg.set.variants) {
      if (!v.available) continue;
      const matches = v.options.filter(o => wanted.includes(String(o).toLowerCase())).length;
      if (matches > score) { best = v; score = matches; }
    }
    return best || cfg.set.variants[0];
  }
  // The set's discount drops its fraction of a cent too: 33.29% of 14.99 takes 4.99. In the dated
  // sale (cfg.set.each, snippets/il-sale.liquid) the set is two items of the sale: the 2-item
  // percentage off each, the sign's and the keychain's.
  const setOff = keychain => cfg.set.each
    ? Math.floor(price() * cfg.set.each / 100 + 1e-4) + Math.floor(keychain.price * cfg.set.each / 100 + 1e-4)
    : Math.floor(keychain.price * cfg.set.percent / 100 + 1e-4);
  const setTotal = () => { const keychain = keychainVariant(); return price() + keychain.price - setOff(keychain); };

  function prices() {
    const each = price();
    picker.querySelectorAll('[data-names-count]').forEach(button => {
      const n = Number(button.dataset.namesCount), percent = percentFor(n);
      button.querySelector('[data-names-total]').textContent = money(unitPrice(each, n) * n);
      button.querySelector('[data-names-save]').textContent = n > 1 && percent ? t('names.save', {percent}) : '';
    });
    const setButton = picker.querySelector('[data-names-set]');
    if (setButton && cfg.set) {
      setButton.querySelector('[data-names-total]').textContent = money(setTotal());
      setButton.querySelector('[data-names-save]').textContent = cfg.set.each ? t('names.save', {percent: cfg.set.each}) : t('names.save_amount', {amount: money(setOff(keychainVariant()))});
    }
    // The bars say what the order costs for what is chosen.
    const total = set ? setTotal() : unitPrice(each, count) * count;
    document.querySelectorAll('.bbprice [data-price], .sbprice [data-price]').forEach(el => { el.textContent = money(total); });
  }

  const addButtons = [...document.querySelectorAll('.add, .buybar button, .sbright button')];
  const plainLabels = new Map();
  function labels() {
    for (const button of addButtons) {
      const node = [...button.childNodes].find(n => n.nodeType === 3 && n.textContent.trim());
      if (!node) continue;
      if (!plainLabels.has(button)) plainLabels.set(button, node.textContent);
      // "Add 2 to cart": as short as the plain words, so it stays on one line in a phone's bar.
      node.textContent = count === 1 && !set ? plainLabels.get(button) : t('names.add_many', {count: set ? 2 : count});
    }
  }

  function drive() {
    if (real.value === names[active]) return;
    real.value = names[active];
    real.dispatchEvent(new Event('input', {bubbles: true}));
  }
  function activate(i) {
    if (i === active && real.value === names[i]) return;
    active = i;
    drive();
    refresh();
  }

  function makeBox(i) {
    const box = realBox.cloneNode(true);
    box.hidden = false;
    box.classList.add('names__box');
    const input = box.querySelector('input');
    for (const attribute of ['name', 'form', 'id', 'data-text-input', 'required']) input.removeAttribute(attribute);
    input.value = names[i];
    input.setAttribute('aria-label', t('names.label', {index: i + 1}));
    const label = document.createElement('span');
    label.className = 'names__boxlabel';
    label.textContent = t('names.label', {index: i + 1});
    box.prepend(label);
    const counter = box.querySelector('[data-count]');
    counter?.removeAttribute('data-count');
    const show = () => { if (counter) { counter.textContent = input.value.length + ' / ' + real.maxLength; counter.className = 'cnt2' + (input.value.length >= real.maxLength ? ' at' : ''); } };
    show();
    input.addEventListener('focus', () => activate(i));
    input.addEventListener('input', () => {
      names[i] = input.value;
      show();
      if (active !== i) activate(i); else { drive(); refresh(); }
      later();
    });
    return {box, input};
  }

  function choose(n, withSet) {
    if (n === count && withSet === set) return;
    if (count === 1) names[0] = real.value;
    count = n;
    set = withSet;
    picker.querySelectorAll('.names__option').forEach(button => button.setAttribute('aria-checked', String(button.hasAttribute('data-names-set') ? set : !set && Number(button.dataset.namesCount) === n)));
    if (n > 1) {
      if (active >= n) active = 0;
      inputs = Array.from({length: n}, (_, i) => makeBox(i));
      boxes.replaceChildren(...inputs.map(made => made.box));
      boxes.hidden = false;
      realBox.hidden = true;
      stripLabel.textContent = t('names.your_names');
      strip.hidden = false;
      drive();
    } else {
      // One name: the first. The others wait in case two or three are chosen again.
      active = 0;
      inputs = [];
      boxes.replaceChildren();
      boxes.hidden = true;
      realBox.hidden = false;
      stripLabel.textContent = t('names.your_set');
      strip.hidden = !set;
      drive();
      if (set) { openHelper(); followName(); followColour(); }
    }
    // The name already drawn keeps its picture now, not when typing stops.
    capture();
    prices();
    labels();
    refresh();
    later();
  }

  function size(mm) {
    const inches = root.querySelector('[data-preview-units][aria-pressed="true"]')?.dataset.previewUnits === 'in';
    return '≈ ' + new Intl.NumberFormat(language, {maximumFractionDigits: 1}).format(inches ? mm / 25.4 : mm / 10) + (inches ? ' in' : ' cm');
  }
  function pictureOf(entry, waiting) {
    const picture = document.createElement('span');
    picture.className = 'names-card__pic';
    if (entry?.picture) {
      const image = new Image();
      image.src = entry.picture;
      image.alt = entry.name;
      picture.append(image);
    } else picture.textContent = waiting;
    return picture;
  }
  function card(i) {
    const name = names[i].trim(), entry = fresh(i) ? checked[i] : null;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'names-card' + (i === active ? ' is-active' : '') + (entry && !entry.review ? ' is-checked' : '');
    button.addEventListener('click', () => activate(i));
    const title = document.createElement('span');
    title.className = 'names-card__name';
    title.textContent = name || t('names.label', {index: i + 1});
    const measure = document.createElement('span');
    measure.className = 'names-card__size';
    measure.textContent = entry ? size(entry.widthMM) : '';
    button.append(pictureOf(entry, name ? t('names.checking') : t('names.type_it')), title, measure);
    return button;
  }
  // The set's two pictures, the sign's and the keychain's, named by what they are.
  function setCard(kind, entry) {
    const box = document.createElement('div');
    box.className = 'names-card' + (entry && !entry.review ? ' is-checked' : '');
    const word = t('object.' + kind);
    const title = document.createElement('span');
    title.className = 'names-card__name';
    title.textContent = word.charAt(0).toLocaleUpperCase(language) + word.slice(1);
    const measure = document.createElement('span');
    measure.className = 'names-card__size';
    measure.textContent = entry ? size(entry.widthMM) : '';
    const typed = names[0].trim();
    box.append(pictureOf(entry, !typed ? t('names.type_it') : kind === 'keychain' && helperProblem() ? '—' : t('names.checking')), title, measure);
    return box;
  }
  function refresh() {
    inputs.forEach(({box}, i) => box.classList.toggle('is-active', i === active));
    if (count > 1) row.replaceChildren(...Array.from({length: count}, (_, i) => card(i)));
    else if (set) row.replaceChildren(setCard('sign', fresh(0) ? checked[0] : null), setCard('keychain', keychainFresh() ? helper.entry : null));
    window.ilCartSync?.();
  }

  // A name the preview has drawn: its line's properties and its picture. Taken each time the
  // preview is ready, so the line is what was drawn last (a foot or a bolt chosen since too).
  async function capture() {
    if ((count < 2 && !set) || root.dataset.state !== 'ready' || !specInput || specInput.disabled) return;
    const i = active, name = names[i].trim();
    let spec;
    try { spec = JSON.parse(specInput.value); } catch { return; }
    if (!name || spec.name !== name) return;
    // The picture first: taking it saves the colours into the spec, as Add to cart does.
    const shot = root.ilCardSnapshot?.();
    try { spec = JSON.parse(specInput.value); } catch { return; }
    if (spec.name !== name) return;
    const properties = {};
    for (const [key, value] of new FormData(form)) if (key.startsWith('properties[') && typeof value === 'string') properties[key.slice(11, -1)] = value;
    const entry = {name, properties, review: !!root.querySelector(reviews), widthMM: spec.widthMM, picture: null};
    checked[i] = entry;
    refresh();
    entry.pending = picture(shot, name, root, specInput).then(url => { entry.picture = url; if (checked[i] === entry) refresh(); });
    await entry.pending;
    later();
  }
  async function picture(shot, name, from, fromSpec) {
    for (let attempt = 0; attempt < 8; attempt++) {
      try { const url = await shot; if (url) return url; } catch { /* the card module may still be loading */ }
      await wait(350);
      try { if (JSON.parse(fromSpec.value).name !== name || from.dataset.state !== 'ready') return null; } catch { return null; }
      shot = from.ilCardSnapshot?.();
    }
    return null;
  }
  new MutationObserver(() => capture()).observe(root, {attributes: true, attributeFilter: ['data-state']});

  // When typing stops, the names not drawn yet are drawn, one at a time.
  function later() { clearTimeout(checkTimer); checkTimer = setTimeout(next, 1200); }
  function next() {
    if (count < 2) return;
    if (root.dataset.state !== 'ready') { later(); return; }
    if (!fresh(active) && valid(names[active].trim())) { capture(); later(); return; }
    const waiting = Array.from({length: count}, (_, i) => i).find(i => !fresh(i) && valid(names[i].trim()));
    if (waiting !== undefined && waiting !== active) activate(waiting);
  }

  /* The set's keychain, drawn by the keychain's own page section in a hidden frame: the same
     page, so the same print shape; no layout, so no page view is counted for it. The shop's
     pages refuse to be framed (X-Frame-Options: DENY, even by the shop itself), so the section
     is fetched and written into the frame (srcdoc), which keeps this page's origin. Made once,
     when the set is first chosen, and kept. */
  let helper = null;
  async function openHelper() {
    if (helper || !cfg.set) return;
    const variant = keychainVariant();
    helper = {frame: null, variant, entry: null, failed: false};
    helper.timer = setTimeout(() => { if (!helper.entry) { helper.failed = true; refresh(); } }, 30000);
    const url = new URL((window.Shopify?.routes?.root || '/') + 'products/' + cfg.set.handle, location.origin);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', cfg.set.section);
    const theme = new URLSearchParams(location.search).get('preview_theme_id');
    if (theme) url.searchParams.set('preview_theme_id', theme);
    try {
      const response = await fetch(url.href, {credentials: 'same-origin'});
      if (!response.ok) throw Error('Keychain section ' + response.status);
      // Its preview only: the cart's and the choices' scripts belong to a page with an address,
      // and the name typed here is drawn first rather than the placeholder.
      const name = real.value.trim();
      const html = (await response.text())
        .replace(/<script\b[^>]*\/(?:il-cart|il-names)\.js\b[^>]*>\s*<\/script>/g, '')
        .replace(/<input\b[^>]*\bdata-text-input\b[^>]*>/, tag => valid(name) ? tag.replace(/\bvalue="[^"]*"/, 'value="' + name.replace(/[&<>"]/g, c => '&#' + c.charCodeAt(0) + ';') + '"') : tag);
      const frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.title = '';
      // Laid out and drawn (its views need a size), but off the screen and invisible.
      frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:420px;height:900px;border:0;opacity:0;pointer-events:none';
      frame.addEventListener('load', attach, {once: true});
      frame.srcdoc = '<!doctype html><html><head><meta charset="utf-8"></head><body>' + html + '</body></html>';
      helper.frame = frame;
      document.body.append(frame);
    } catch {
      helper.failed = true;
      refresh();
    }
  }
  function attach() {
    try {
      const doc = helper.frame.contentDocument, win = helper.frame.contentWindow;
      const helperRoot = doc?.querySelector('[data-union-preview]');
      const helperForm = doc?.getElementById('il-buy');
      const helperInput = doc?.querySelector('[data-text-input][form="il-buy"]');
      const helperSpec = helperRoot?.querySelector('[data-keychain-spec],[data-magnet-spec]');
      if (!helperRoot || !helperForm || !helperInput || !helperSpec) throw Error('No keychain preview');
      Object.assign(helper, {doc, win, root: helperRoot, form: helperForm, input: helperInput, spec: helperSpec});
      new win.MutationObserver(() => { captureKeychain(); refresh(); }).observe(helperRoot, {attributes: true, attributeFilter: ['data-state']});
      followName();
      followColour();
      captureKeychain();
    } catch {
      helper.failed = true;
      refresh();
    }
  }
  function followName() {
    if (!helper?.input || helper.input.value === real.value) return;
    helper.input.value = real.value;
    helper.input.dispatchEvent(new helper.win.Event('input', {bubbles: true}));
  }
  // The keychain's colour chips, pressed as its own page presses them (aria-pressed is what
  // its preview reads), then the click its preview listens for.
  function followColour() {
    if (!helper?.doc) return;
    const target = keychainVariant();
    helper.variant = target;
    const chips = [...helper.doc.querySelectorAll('[data-opt-position][data-opt-value]')];
    let moved = false;
    for (const chip of chips) {
      const on = String(target.options[Number(chip.dataset.optPosition) - 1] ?? '').toLowerCase() === String(chip.dataset.optValue).toLowerCase();
      if ((chip.getAttribute('aria-pressed') === 'true') !== on) { chip.setAttribute('aria-pressed', String(on)); moved = true; }
    }
    const id = helper.form.querySelector('[data-variant-id]');
    if (id) id.value = target.id;
    if (!moved) return;
    helper.entry = null;
    chips.find(chip => chip.getAttribute('aria-pressed') === 'true')?.click();
    setTimeout(captureKeychain, 300);
    refresh();
  }
  const keychainFresh = () => !!helper?.entry && helper.entry.name === names[0].trim() && helper.entry.variant === keychainVariant().id;
  const helperProblem = () => !helper || helper.failed || ['invalid', 'error'].includes(helper.root?.dataset.state);
  async function captureKeychain() {
    if (!set || !helper?.root || helper.root.dataset.state !== 'ready' || helper.spec.disabled) return;
    const name = real.value.trim();
    let spec;
    try { spec = JSON.parse(helper.spec.value); } catch { return; }
    if (!name || spec.name !== name) return;
    const shot = helper.root.ilCardSnapshot?.();
    try { spec = JSON.parse(helper.spec.value); } catch { return; }
    if (spec.name !== name) return;
    const properties = {};
    for (const [key, value] of new helper.win.FormData(helper.form)) if (key.startsWith('properties[') && typeof value === 'string') properties[key.slice(11, -1)] = value;
    const review = !!helper.root.querySelector('[data-keychain-review]:not(:disabled),[data-magnet-review]:not(:disabled)');
    const entry = {name, properties, review, widthMM: spec.widthMM, picture: null, variant: helper.variant.id};
    helper.entry = entry;
    clearTimeout(helper.timer);
    refresh();
    entry.pending = picture(shot, name, helper.root, helper.spec).then(url => { entry.picture = url; if (helper.entry === entry) refresh(); });
  }
  // At Add: wait while what is chosen is still being drawn, up to a limit; a failed preview, or
  // the set's keychain that cannot be drawn here, is not waited for.
  const SPEC_FIELDS = ['_Sign preview spec', '_Keychain preview spec', '_Magnet preview spec'];
  const NOT_SHOWN = 'Not shown on the customer\'s device: make it from the typed name';
  const textKey = (real.getAttribute('name') || 'properties[Name]').slice(11, -1);
  function settle(limit) {
    const working = () => ['loading', 'updating', 'ready'].includes(root.dataset.state);
    const signDrawn = () => {
      try { return root.dataset.state === 'ready' && !specInput.disabled && JSON.parse(specInput.value).name === names[0].trim(); } catch { return true; }
    };
    const keychainDone = () => !helper || keychainFresh() || helperProblem();
    const done = () => {
      if (count > 1) return !working() || Array.from({length: count}, (_, i) => fresh(i)).every(Boolean);
      if (set) return (!working() || signDrawn()) && keychainDone();
      return true;
    };
    return new Promise(resolve => {
      const end = Date.now() + limit;
      const tick = () => (done() || Date.now() > end ? resolve() : setTimeout(tick, 150));
      tick();
    });
  }
  real.addEventListener('input', () => {
    if (count > 1) return;
    names[0] = real.value;
    if (set) { followName(); refresh(); }
  });

  // A colour changes every picture and saved colours: each name is checked again, and the
  // set's keychain takes the colour too.
  form.addEventListener('click', event => {
    if (!event.target.closest('[data-opt-position]')) return;
    setTimeout(() => {
      prices();
      if (count > 1) { checked.fill(null); refresh(); setTimeout(capture, 300); later(); }
      if (set) { checked[0] = null; followColour(); setTimeout(capture, 300); refresh(); }
    }, 0);
  });
  root.querySelectorAll('[data-preview-units]').forEach(button => button.addEventListener('click', () => setTimeout(refresh, 0)));
  picker.querySelectorAll('[data-names-count]').forEach(button => button.addEventListener('click', () => choose(Number(button.dataset.namesCount), false)));
  picker.querySelector('[data-names-set]')?.addEventListener('click', () => choose(1, true));

  window.ilNames = {
    active: () => count > 1 || set,
    many: () => count > 1,
    // What keeps Add off (il-cart.js): two or three names not all typed, or a name the design
    // cannot take; the set, when its keychain refuses the name. Never a drawing not finished or
    // a preview that failed (owner, 22 October 2026: "I want the customer to be able to order it
    // even if it does not work"): lines() waits a little, then sends the typed name.
    issue() {
      if (count > 1) {
        for (let i = 0; i < count; i++) {
          const name = names[i].trim();
          if (!name) return t('names.type_all', {count});
          if (!valid(name)) return name.length < fewest ? t('preview.min_letters', {count: fewest}) : real.title || t('names.type_all', {count});
        }
        return '';
      }
      if (set && helper?.root?.dataset.state === 'invalid') return t('names.set_name');
      return '';
    },
    // Asked by il-cart.js at Add: one line per item. The names and the set's keychain still
    // being drawn are waited for, a few seconds, and a picture still being made a moment; an item
    // not drawn by then goes in with its typed name and _Preview, made from the name later.
    async lines() {
      await settle(8000);
      const typedOnly = name => ({[textKey]: name, _Preview: NOT_SHOWN});
      if (set) {
        const shot = root.ilCardSnapshot?.();
        const properties = {};
        for (const [key, value] of new FormData(form)) if (key.startsWith('properties[') && typeof value === 'string') properties[key.slice(11, -1)] = value;
        if (!SPEC_FIELDS.some(field => properties[field])) properties._Preview = NOT_SHOWN;
        const keychain = keychainFresh() ? helper.entry : null;
        const [signPicture] = await Promise.all([Promise.race([Promise.resolve(shot).catch(() => null), wait(1500)]), keychain ? Promise.race([keychain.pending, wait(1500)]) : null]);
        return [{id: Number(variantId()), properties, picture: signPicture},
          keychain ? {id: keychain.variant, properties: keychain.properties, picture: keychain.picture} : {id: keychainVariant().id, properties: typedOnly(names[0].trim()), picture: null}];
      }
      const entries = Array.from({length: count}, (_, i) => fresh(i) ? checked[i] : null);
      await Promise.race([Promise.all(entries.filter(Boolean).map(entry => entry.pending)), wait(1500)]);
      return entries.map((entry, i) => entry ? {properties: entry.properties, picture: entry.picture} : {properties: typedOnly(names[i].trim()), picture: null});
    },
    added() { /* the names stay, as one name stays after Add to cart */ },
  };
  prices();
  window.ilCartSync?.();
}
